import { Link } from "react-router-dom";
import { useEffect } from "react";
import { ArrowUpRight, MapPin } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { CTABand } from "@/components/CTABand";
import { Seo, localBusinessSchema, breadcrumbSchema } from "@/seo/Seo";
import { SERVICES, BUSINESS } from "@/data/servicesData";
import { AREAS } from "@/data/areasData";

export default function ServicesIndex() {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const crumbs = [{ name: "Home", path: "/" }, { name: "Services", path: "/services" }];

  return (
    <div className="bg-white min-h-screen" data-testid="services-index">
      <Seo
        title="Handyman & DIY Services in Swansea | FIT N FIX DIY Services"
        description="The full range of handyman & DIY services across Swansea & Gower — flat-pack assembly, TV mounting, painting, gutter cleaning, fencing, repairs & more. £60 minimum per job."
        path="/services"
        keywords={["handyman services swansea", "diy services swansea", "handyman near me swansea", "odd jobs swansea"]}
        jsonLd={[localBusinessSchema(), breadcrumbSchema(crumbs)]}
      />
      <Navbar />

      <section className="pt-[110px] sm:pt-[130px] pb-12 blueprint">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
          <Breadcrumbs items={crumbs} />
          <h1 className="font-display font-black text-[#0A1A4A] text-4xl sm:text-6xl lg:text-7xl tracking-tight leading-[0.92] mt-5 max-w-4xl">
            HANDYMAN & DIY SERVICES<br /><span className="text-[#2563EB]">ACROSS SWANSEA</span>
          </h1>
          <p className="text-lg text-[#0A1A4A]/70 mt-6 max-w-2xl">
            One local team for all your home jobs — from flat-pack builds to full room repaints. Every job carries a £{BUSINESS.minCharge} minimum charge, with your final price quoted by email. Pick a service to learn more.
          </p>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-5 sm:px-8 pb-16">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {SERVICES.map((s) => (
            <Link key={s.slug} to={`/services/${s.slug}`} className="group border-2 border-[#0A1A4A] overflow-hidden hover:shadow-hard transition-shadow bg-white" data-testid={`index-service-${s.slug}`}>
              <div className="h-44 overflow-hidden border-b-2 border-[#0A1A4A]">
                <img src={s.image} alt={`${s.name} Swansea`} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              </div>
              <div className="p-6">
                <div className="flex items-start justify-between gap-2">
                  <h2 className="font-display font-extrabold uppercase text-xl text-[#0A1A4A]">{s.name}</h2>
                  <ArrowUpRight className="text-[#2563EB] shrink-0" />
                </div>
                <p className="text-sm text-[#0A1A4A]/70 mt-2">{s.intro.slice(0, 120)}…</p>
                <div className="mt-4 font-mono-accent text-[11px] tracking-widest uppercase text-[#2563EB]">{s.contactForPricing ? "Contact for pricing" : `£${BUSINESS.minCharge} min`} · Learn more</div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <section className="bg-[#0A1A4A] py-16">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
          <div className="font-mono-accent text-xs tracking-widest uppercase text-[#EAB308] mb-3">/ Coverage</div>
          <h2 className="font-display font-black text-white text-3xl sm:text-5xl uppercase tracking-tight mb-8">Areas We Serve</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {AREAS.map((a) => (
              <Link key={a.slug} to={`/areas/${a.slug}`} className="group border-2 border-white/20 p-5 hover:bg-white transition-colors" data-testid={`index-area-${a.slug}`}>
                <MapPin className="text-[#2563EB] mb-3" />
                <div className="font-display font-extrabold uppercase text-white group-hover:text-[#0A1A4A] transition-colors">{a.area}</div>
                <div className="font-mono-accent text-[11px] tracking-widest text-white/50 group-hover:text-[#0A1A4A]/60 transition-colors mt-1">{a.postcode}</div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <CTABand />
      <Footer />
    </div>
  );
}
